/**
 * LoadingOverlay Component implemented with Material UI
 *
 * This component provides a semi-transparent overlay with loading content.
 * Can be used with or without children for flexible loading states.
 */
import React from 'react';
import { Box, CircularProgress, Typography, styled, BoxProps } from '@mui/material';
import { LoadingOverlayProps } from 'types/components/atoms';

/**
 * LoadingOverlay component
 *
 * @param props - Component props
 * @returns A loading overlay component
 */
const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible = true,
  label,
  size = 24,
  children
}) => {
  if (!visible) {
    return null;
  }

  return (
    <OverlayWrapper>
      {children || (
        <>
          <CircularProgress
            size={size}
            thickness={5}
            sx={{ color: 'text.primary' }}
            aria-label={label || 'Loading...'}
          />
          {label && (
            <Typography sx={{
              color: 'text.primary',
              fontSize: '14px',
              fontWeight: 600,
              marginTop: '8px'
            }}>
              {label}
            </Typography>
          )}
        </>
      )}
    </OverlayWrapper>
  );
};

// Styled components using MUI styled API
const OverlayWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  zIndex: 1000,
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  }),
}));

export type { LoadingOverlayProps };
export default LoadingOverlay;
